// updateVerificado.js — un UPDATE que no tocó ninguna fila deja de ser un éxito.
//
// EL PROBLEMA: PostgREST responde `error: null` cuando un UPDATE no matchea nada.
// Da igual si fue por un id inexistente o porque RLS lo filtró: desde el cliente es
// indistinguible de un guardado bueno. El código concluye que guardó, muestra el toast
// de éxito, y el cambio no está. Es la causa del bug "Marcar listo manualmente".
//
// LA SOLUCIÓN: pedir las filas de vuelta con .select() y tratar el conjunto vacío como
// error. Verificado contra pg_policies antes de escribir esto: en las tablas donde se
// aplica, las políticas de SELECT son iguales o más amplias que las de UPDATE, así que
// quien puede escribir puede leer y no hay falsos negativos.
//
// NO reemplaza a los permisos: avisar bien de un permiso que falta y dar el permiso que
// corresponde son dos arreglos distintos. Este helper hace lo primero, siempre.

// Voz de STAFF (no la del portal del cliente): directa, sin jerga, sin culpar a nadie.
// Lo que más importa que se lea es que NO se guardó nada — el resto es contexto.
export const SIN_FILAS_MENSAJE = 'No se guardó el cambio: no tienes permiso sobre este registro o ya no existe.';

export const SIN_FILAS_CODE = 'NO_ROWS';

function mensajeCon(queSeGuardaba) {
  if (!queSeGuardaba) return SIN_FILAS_MENSAJE;
  return `No se guardó ${queSeGuardaba}: no tienes permiso sobre este registro o ya no existe.`;
}

/**
 * UPDATE con verificación de filas afectadas.
 *
 * @param supabase       cliente de supabase
 * @param tabla          nombre de la tabla
 * @param payload        cambios a aplicar
 * @param aplicarFiltro  (query) => query — dónde encadenar .eq()/.in()/lo que haga falta
 * @param opciones.queSeGuardaba  texto corto para el mensaje ("el entregable", "el cliente")
 * @returns { ok: true, rows } | { ok: false, error: { message, code }, sinFilas: boolean }
 */
export async function updateVerificado(supabase, tabla, payload, aplicarFiltro, opciones = {}) {
  const consulta = aplicarFiltro(supabase.from(tabla).update(payload));
  const { data, error } = await consulta.select('id');

  if (error) {
    // El detalle técnico va al log, nunca a pantalla.
    console.error(`[updateVerificado] ${tabla} rechazó el update:`, error.message || error);
    return { ok: false, error, sinFilas: false };
  }

  if (!data || data.length === 0) {
    const message = mensajeCon(opciones.queSeGuardaba);
    console.warn(`[updateVerificado] ${tabla}: UPDATE de 0 filas (RLS o registro inexistente).`);
    return { ok: false, error: { message, code: SIN_FILAS_CODE }, sinFilas: true };
  }

  return { ok: true, rows: data };
}

/** Atajo para el caso habitual: filtrar por una sola columna (casi siempre `id`). */
export async function updatePorId(supabase, tabla, id, payload, opciones = {}) {
  const columna = opciones.columna || 'id';
  return await updateVerificado(supabase, tabla, payload, (q) => q.eq(columna, id), opciones);
}

// ── DELETE ───────────────────────────────────────────────────────────────────
// Un DELETE tiene el mismo agujero que el UPDATE, y encima es peor de leer: la
// UI suele quitar la fila de la lista antes de saber nada, así que un borrado
// que RLS rechazó se ve exactamente igual que uno que ocurrió — hasta que
// alguien recarga y el registro sigue ahí.
//
// La ausencia previa NO es lo mismo que el rechazo, pero desde el cliente se
// ven iguales: las dos devuelven cero filas. Por eso `idempotente` es una
// decisión que se toma por escrito en cada llamada, no un accidente.

export const NADA_QUE_BORRAR_MENSAJE = 'No se borró: no tienes permiso sobre este registro o ya no existe.';

function mensajeBorradoCon(queSeBorraba) {
  if (!queSeBorraba) return NADA_QUE_BORRAR_MENSAJE;
  return `No se borró ${queSeBorraba}: no tienes permiso sobre este registro o ya no existe.`;
}

/**
 * DELETE con verificación de filas afectadas.
 *
 * @param opciones.queSeBorraba  texto corto para el mensaje ("el miembro del equipo")
 * @param opciones.idempotente   true si "ya no estaba" cuenta como éxito. Es una
 *                               decisión explícita: sin ella, cero filas es fallo.
 * @returns { ok: true, rows } | { ok: false, error: { message, code }, sinFilas: boolean }
 */
export async function borrarVerificado(supabase, tabla, aplicarFiltro, opciones = {}) {
  const consulta = aplicarFiltro(supabase.from(tabla).delete());
  const { data, error } = await consulta.select('id');

  if (error) {
    console.error(`[borrarVerificado] ${tabla} rechazó el delete:`, error.message || error);
    return { ok: false, error, sinFilas: false };
  }

  if (!data || data.length === 0) {
    if (opciones.idempotente) return { ok: true, rows: [], yaNoEstaba: true };
    const message = mensajeBorradoCon(opciones.queSeBorraba);
    console.warn(`[borrarVerificado] ${tabla}: DELETE de 0 filas (RLS o registro inexistente).`);
    return { ok: false, error: { message, code: SIN_FILAS_CODE }, sinFilas: true };
  }

  return { ok: true, rows: data };
}

/** Atajo para borrar por una sola columna (casi siempre `id`). */
export async function borrarPorId(supabase, tabla, id, opciones = {}) {
  const columna = opciones.columna || 'id';
  return await borrarVerificado(supabase, tabla, (q) => q.eq(columna, id), opciones);
}
